import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";
import Header from "@/components/Header";
import { useApp } from "@/context/AppContext";
import { gbp2 } from "@/lib/api";

export default function Cart() {
  const { cart, removeFromCart, updateQty, subtotal, depositTotal, remaining, totalDuration, refreshCart } = useApp();
  const nav = useNavigate();

  // Refresh cart lines against the live treatment records so any admin-side
  // updates (duration / price / deposit) are reflected immediately.
  useEffect(() => { refreshCart().catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Header />
      <main className="max-w-4xl mx-auto px-6 lg:px-10 pt-10 pb-24">
        <p className="mono-label mb-3">Your selection</p>
        <h1 className="font-serif text-5xl font-light text-stone-900">Cart</h1>

        {cart.length === 0 ? (
          <div className="mt-12 border hairline p-12 text-center bg-white">
            <p className="font-serif text-2xl text-stone-900">Your cart is empty</p>
            <p className="mt-2 text-stone-600 text-sm">Explore the treatments to add your first appointment.</p>
            <Link to="/" data-testid="continue-shopping-empty" className="mt-6 inline-block border border-stone-900 px-6 py-3 mono-label hover:bg-stone-900 hover:text-white transition">
              Browse Treatments
            </Link>
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 border hairline bg-white">
              {cart.map((i) => (
                <div key={i.treatment_id} className="flex flex-col sm:flex-row sm:items-center gap-4 p-6 border-b hairline last:border-b-0" data-testid={`cart-item-${i.treatment_id}`}>
                  <div className="flex-1">
                    <p className="font-serif text-xl text-stone-900">{i.name}</p>
                    {i.consultation_target_label && (
                      <p className="text-xs mt-1 text-[#8A6D2A]" data-testid={`cart-consultation-target-${i.treatment_id}`}>
                        Consultation for · <span className="font-medium">{i.consultation_target_label}</span>
                      </p>
                    )}
                    <p className="mono-label mt-1">{i.duration_min} min{(i.consultation_target_label || (i.category || "").toLowerCase() === "consultation") ? "" : ` · Deposit £${Number(i.deposit_value).toFixed(0)}${(i.deposit_type === "full") ? " (full amount)" : ""}`}</p>
                  </div>
                  {i.allow_quantity ? (
                    <div className="flex items-center gap-3 border hairline">
                      <button data-testid={`qty-minus-${i.treatment_id}`} onClick={() => updateQty(i.treatment_id, i.quantity - 1)} className="px-3 py-2 hover:bg-alt"><Minus size={14} /></button>
                      <span className="min-w-6 text-center">{i.quantity}</span>
                      <button data-testid={`qty-plus-${i.treatment_id}`} onClick={() => updateQty(i.treatment_id, i.quantity + 1)} className="px-3 py-2 hover:bg-alt"><Plus size={14} /></button>
                    </div>
                  ) : (
                    <span className="mono-label">Qty 1</span>
                  )}
                  <span className="font-serif text-xl w-24 text-right">{gbp2(i.price * i.quantity)}</span>
                  <button data-testid={`remove-${i.treatment_id}`} onClick={() => removeFromCart(i.treatment_id)} className="text-stone-500 hover:text-red-700"><Trash2 size={16} /></button>
                </div>
              ))}
              <div className="p-6 bg-alt">
                <Link to="/" data-testid="continue-shopping" className="mono-label text-stone-700 hover:text-stone-900">← Continue shopping</Link>
              </div>
            </div>

            <aside className="border hairline bg-white p-6 h-fit">
              <p className="mono-label mb-4">Summary</p>
              <Row label="Total duration" value={`${totalDuration} min`} />
              <Row label="Subtotal" value={gbp2(subtotal)} />
              <Row label="Deposit today" value={gbp2(depositTotal)} bold />
              <Row label="Balance in clinic" value={gbp2(remaining)} />
              <button
                data-testid="proceed-booking"
                onClick={() => nav("/booking")}
                className="mt-6 w-full bg-stone-900 text-white py-4 mono-label hover:bg-[#C5A059] transition"
                style={{ color: "#fff" }}
              >
                Continue to Booking
              </button>
            </aside>
          </div>
        )}
      </main>
    </>
  );
}

const Row = ({ label, value, bold }) => (
  <div className={`flex justify-between py-2 ${bold ? "font-medium text-stone-900" : "text-stone-600"}`}>
    <span className="mono-label" style={bold ? { color: "#141414" } : {}}>{label}</span>
    <span className={bold ? "font-serif text-2xl" : "text-sm"}>{value}</span>
  </div>
);
